import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  FiArrowLeft,
  FiCalendar,
  FiPieChart,
  FiBarChart2,
  FiTrendingUp,
} from 'react-icons/fi';
import { toast } from 'react-toastify';
import invoiceService from '../services/invoiceService';
import Button from '../components/Button';
import './TechnicianForm.css';

const RevenueReports = () => {
  const [loading, setLoading] = useState(true);
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [paymentStatus, setPaymentStatus] = useState('');
  const [reports, setReports] = useState({
    overall: {
      totalInvoices: 0,
      totalRevenue: 0,
      totalReceived: 0,
      totalOutstanding: 0,
    },
    byServiceType: [],
    byMonth: [],
    byPaymentStatus: [],
  });

  useEffect(() => {
    fetchReports();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [startDate, endDate, paymentStatus]);

  const fetchReports = async () => {
    setLoading(true);
    try {
      const params = {};
      if (startDate) params.startDate = startDate;
      if (endDate) params.endDate = endDate;
      if (paymentStatus) params.paymentStatus = paymentStatus;
      const response = await invoiceService.getRevenueReports(params);
      setReports(response.data);
    } catch (error) {
      toast.error('Failed to fetch revenue reports');
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount) =>
    new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      maximumFractionDigits: 0,
    }).format(amount || 0);

  const getMonthName = (month) => {
    const months = [
      'January',
      'February',
      'March',
      'April',
      'May',
      'June',
      'July',
      'August',
      'September',
      'October',
      'November',
      'December',
    ];
    return months[month - 1];
  };

  const getMaxAmount = (arr, field) => {
    if (!arr || arr.length === 0) return 1;
    return Math.max(...arr.map((item) => item[field] || 0));
  };

  if (loading) {
    return (
      <div className="loading-container">
        <div className="spinner" />
      </div>
    );
  }

  const { overall, byServiceType, byMonth, byPaymentStatus: byStatus } = reports;

  return (
    <div className="page-container">
      <div className="page-header">
        <div>
          <h2 className="page-title">Revenue Reports</h2>
          <p className="page-subtitle">
            Analyze revenue by service type, period, and payment status
          </p>
        </div>
        <Link to="/invoices">
          <Button variant="secondary" icon={<FiArrowLeft />}>
            Back to Invoices
          </Button>
        </Link>
      </div>

      {/* Filters */}
      <div
        className="page-filters"
        style={{
          marginBottom: '2rem',
          padding: '1rem',
          background: '#f9fafb',
          borderRadius: '8px',
        }}
      >
        <div
          style={{
            display: 'flex',
            gap: '1rem',
            alignItems: 'center',
            flexWrap: 'wrap',
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
            <FiCalendar />
            <input
              type="date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              className="filter-select"
              placeholder="Start Date"
            />
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
            <FiCalendar />
            <input
              type="date"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              className="filter-select"
              placeholder="End Date"
            />
          </div>
          <div className="input-group">
            <label className="input-label">Payment Status</label>
            <select
              value={paymentStatus}
              onChange={(e) => setPaymentStatus(e.target.value)}
              className="filter-select"
            >
              <option value="">All</option>
              <option value="unpaid">Unpaid</option>
              <option value="partial">Partial</option>
              <option value="paid">Paid</option>
            </select>
          </div>
          <Button
            variant="secondary"
            onClick={() => {
              setStartDate('');
              setEndDate('');
              setPaymentStatus('');
            }}
          >
            Clear Filters
          </Button>
        </div>
      </div>

      {/* Overall summary */}
      <div
        style={{
          marginBottom: '2rem',
          padding: '2rem',
          background: 'linear-gradient(135deg, #22c55e 0%, #16a34a 100%)',
          borderRadius: '12px',
          color: 'white',
        }}
      >
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            flexWrap: 'wrap',
            gap: '1rem',
          }}
        >
          <div>
            <p
              style={{
                margin: 0,
                opacity: 0.9,
                fontSize: '0.875rem',
              }}
            >
              Total Revenue
            </p>
            <h2
              style={{
                margin: '0.5rem 0',
                fontSize: '2.5rem',
                fontWeight: 'bold',
              }}
            >
              {formatCurrency(overall.totalRevenue)}
            </h2>
            <p
              style={{
                margin: '0.25rem 0 0 0',
                fontSize: '0.875rem',
                opacity: 0.85,
              }}
            >
              {overall.totalInvoices} invoice
              {overall.totalInvoices !== 1 ? 's' : ''} • Received{' '}
              {formatCurrency(overall.totalReceived)} • Outstanding{' '}
              {formatCurrency(overall.totalOutstanding)}
            </p>
          </div>
          <div style={{ fontSize: '3rem', opacity: 0.35 }}>
            <FiTrendingUp />
          </div>
        </div>
      </div>

      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(380px, 1fr))',
          gap: '2rem',
        }}
      >
        {/* Revenue by service type */}
        <div
          style={{
            background: 'white',
            borderRadius: '12px',
            padding: '1.5rem',
            boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
          }}
        >
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem',
              marginBottom: '1.5rem',
            }}
          >
            <FiPieChart style={{ fontSize: '1.5rem', color: '#3b82f6' }} />
            <h3
              style={{
                margin: 0,
                fontSize: '1.25rem',
                fontWeight: '600',
              }}
            >
              By Service Type
            </h3>
          </div>

          {byServiceType.length === 0 ? (
            <p
              style={{
                color: '#6b7280',
                textAlign: 'center',
                padding: '2rem',
              }}
            >
              No invoices found
            </p>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
              {byServiceType.map((item, index) => {
                const maxAmount = getMaxAmount(byServiceType, 'totalRevenue');
                const barWidth = ((item.totalRevenue || 0) / maxAmount) * 100;
                const percentage =
                  overall.totalRevenue > 0
                    ? ((item.totalRevenue || 0) / overall.totalRevenue) * 100
                    : 0;

                return (
                  <div key={item.serviceType || index} style={{ marginBottom: '0.5rem' }}>
                    <div
                      style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        marginBottom: '0.25rem',
                      }}
                    >
                      <span
                        style={{
                          fontWeight: 500,
                          textTransform: 'capitalize',
                        }}
                      >
                        {item.serviceType || 'Other'}
                      </span>
                      <span
                        style={{
                          fontWeight: 600,
                          color: '#111827',
                        }}
                      >
                        {formatCurrency(item.totalRevenue)}
                      </span>
                    </div>
                    <div
                      style={{
                        width: '100%',
                        height: 8,
                        background: '#e5e7eb',
                        borderRadius: 4,
                        overflow: 'hidden',
                      }}
                    >
                      <div
                        style={{
                          width: `${barWidth}%`,
                          height: '100%',
                          background: '#3b82f6',
                          borderRadius: 4,
                          transition: 'width 0.3s ease',
                        }}
                      />
                    </div>
                    <div
                      style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        marginTop: '0.25rem',
                        fontSize: '0.875rem',
                        color: '#6b7280',
                      }}
                    >
                      <span>
                        {item.count} invoice{item.count !== 1 ? 's' : ''}
                      </span>
                      <span>{percentage.toFixed(1)}%</span>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Revenue by month */}
        <div
          style={{
            background: 'white',
            borderRadius: '12px',
            padding: '1.5rem',
            boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
          }}
        >
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem',
              marginBottom: '1.5rem',
            }}
          >
            <FiBarChart2 style={{ fontSize: '1.5rem', color: '#10b981' }} />
            <h3
              style={{
                margin: 0,
                fontSize: '1.25rem',
                fontWeight: '600',
              }}
            >
              By Month
            </h3>
          </div>

          {byMonth.length === 0 ? (
            <p
              style={{
                color: '#6b7280',
                textAlign: 'center',
                padding: '2rem',
              }}
            >
              No invoices found
            </p>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
              {byMonth.map((month) => {
                const maxAmount = getMaxAmount(byMonth, 'totalRevenue');
                const barWidth = ((month.totalRevenue || 0) / maxAmount) * 100;
                const label = `${getMonthName(month._id.month)} ${month._id.year}`;

                return (
                  <div
                    key={`${month._id.year}-${month._id.month}`}
                    style={{ marginBottom: '0.5rem' }}
                  >
                    <div
                      style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        marginBottom: '0.25rem',
                      }}
                    >
                      <span style={{ fontWeight: 500 }}>{label}</span>
                      <span
                        style={{
                          fontWeight: 600,
                          color: '#111827',
                        }}
                      >
                        {formatCurrency(month.totalRevenue)}
                      </span>
                    </div>
                    <div
                      style={{
                        width: '100%',
                        height: 8,
                        background: '#e5e7eb',
                        borderRadius: 4,
                        overflow: 'hidden',
                      }}
                    >
                      <div
                        style={{
                          width: `${barWidth}%`,
                          height: '100%',
                          background: '#10b981',
                          borderRadius: 4,
                          transition: 'width 0.3s ease',
                        }}
                      />
                    </div>
                    <div
                      style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        marginTop: '0.25rem',
                        fontSize: '0.875rem',
                        color: '#6b7280',
                      }}
                    >
                      <span>
                        {month.count} invoice{month.count !== 1 ? 's' : ''}
                      </span>
                      <span>
                        Avg:{' '}
                        {formatCurrency(
                          (month.totalRevenue || 0) / (month.count || 1),
                        )}
                      </span>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>

      {/* Payment status breakdown & quick stats */}
      <div
        style={{
          marginTop: '2rem',
          display: 'grid',
          gridTemplateColumns: 'minmax(260px, 1.2fr) minmax(260px, 1fr)',
          gap: '2rem',
        }}
      >
        <div
          style={{
            background: 'white',
            borderRadius: '12px',
            padding: '1.5rem',
            boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
          }}
        >
          <h3
            style={{
              margin: '0 0 1rem 0',
              fontSize: '1.25rem',
              fontWeight: '600',
            }}
          >
            Payment Status Breakdown
          </h3>
          {byStatus.length === 0 ? (
            <p
              style={{
                color: '#6b7280',
                textAlign: 'center',
                padding: '1.5rem',
              }}
            >
              No invoices found
            </p>
          ) : (
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
                gap: '1rem',
              }}
            >
              {byStatus.map((s) => (
                <div
                  key={s.paymentStatus}
                  style={{
                    padding: '1rem',
                    borderRadius: '0.75rem',
                    border: '1px solid #e5e7eb',
                    background: '#f9fafb',
                  }}
                >
                  <p
                    style={{
                      margin: 0,
                      fontSize: '0.85rem',
                      color: '#6b7280',
                      textTransform: 'uppercase',
                      letterSpacing: '0.03em',
                    }}
                  >
                    {s.paymentStatus || 'unknown'}
                  </p>
                  <p
                    style={{
                      margin: '0.25rem 0 0 0',
                      fontSize: '1.25rem',
                      fontWeight: 700,
                    }}
                  >
                    {formatCurrency(s.totalRevenue)}
                  </p>
                  <p
                    style={{
                      margin: '0.25rem 0 0 0',
                      fontSize: '0.8rem',
                      color: '#6b7280',
                    }}
                  >
                    {s.count} invoice{s.count !== 1 ? 's' : ''}
                  </p>
                </div>
              ))}
            </div>
          )}
        </div>

        <div
          style={{
            background: 'white',
            borderRadius: '12px',
            padding: '1.5rem',
            boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
          }}
        >
          <h3
            style={{
              margin: '0 0 1rem 0',
              fontSize: '1.25rem',
              fontWeight: '600',
            }}
          >
            Summary Statistics
          </h3>
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: '1fr',
              gap: '1rem',
            }}
          >
            <div>
              <p
                style={{
                  margin: 0,
                  fontSize: '0.85rem',
                  color: '#6b7280',
                }}
              >
                Average Invoice Value
              </p>
              <p
                style={{
                  margin: '0.25rem 0 0 0',
                  fontSize: '1.25rem',
                  fontWeight: 600,
                }}
              >
                {formatCurrency(
                  overall.totalRevenue / (overall.totalInvoices || 1),
                )}
              </p>
            </div>
            {byServiceType.length > 0 && (
              <div>
                <p
                  style={{
                    margin: 0,
                    fontSize: '0.85rem',
                    color: '#6b7280',
                  }}
                >
                  Top Service Type
                </p>
                <p
                  style={{
                    margin: '0.25rem 0 0 0',
                    fontSize: '1.1rem',
                    fontWeight: 600,
                    textTransform: 'capitalize',
                  }}
                >
                  {byServiceType[0].serviceType || 'Other'}
                </p>
                <p
                  style={{
                    margin: '0.25rem 0 0 0',
                    fontSize: '0.85rem',
                    color: '#6b7280',
                  }}
                >
                  {formatCurrency(byServiceType[0].totalRevenue)} across{' '}
                  {byServiceType[0].count} invoice
                  {byServiceType[0].count !== 1 ? 's' : ''}
                </p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default RevenueReports;

